import React, { useEffect, useRef, useState } from 'react';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    website: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [formStartedAt, setFormStartedAt] = useState(Date.now());
  const captchaContainerRef = useRef(null);
  const captchaWidgetIdRef = useRef(null);

  const renderCaptcha = () => {
    if (!TURNSTILE_SITE_KEY || !window.turnstile || !captchaContainerRef.current) return;
    if (captchaWidgetIdRef.current !== null) {
      try {
        window.turnstile.remove(captchaWidgetIdRef.current);
      } catch (_) {
        // no-op
      }
      captchaWidgetIdRef.current = null;
    }
    captchaContainerRef.current.innerHTML = '';
    captchaWidgetIdRef.current = window.turnstile.render(captchaContainerRef.current, {
      sitekey: TURNSTILE_SITE_KEY,
      callback: (token) => setCaptchaToken(token || ''),
      'expired-callback': () => setCaptchaToken(''),
      'error-callback': () => setCaptchaToken(''),
    });
  };

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    const existing = document.getElementById('cf-turnstile-script');
    if (existing) {
      if (window.turnstile) renderCaptcha();
      return;
    }

    const script = document.createElement('script');
    script.id = 'cf-turnstile-script';
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = () => renderCaptcha();
    document.body.appendChild(script);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (TURNSTILE_SITE_KEY && !captchaToken) {
      setError('Please complete captcha verification.');
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/contact`, {
        ...formData,
        captcha_token: captchaToken,
        form_started_at: formStartedAt,
      });
      
      if (response.status === 200) {
        setSuccess(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          website: ''
        });
        setCaptchaToken('');
        setFormStartedAt(Date.now());
        renderCaptcha();
        
        // Reset success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.detail || 'Failed to submit form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact-form" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#8B2820] mb-4">
              Get in Touch
            </h2>
            <p className="text-xl text-[#4a3a33]">
              Have questions about our products? We'd love to hear from you.
            </p>
          </div>

          <Card className="border-none shadow-2xl">
            <CardContent className="p-8 md:p-12">
              {success && (
                <div className="mb-8 p-6 bg-green-50 border border-green-200 rounded-lg flex items-start">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-green-800 mb-1">
                      Thank you for contacting us!
                    </h3>
                    <p className="text-green-700">
                      We've received your message and will get back to you within 24-48 hours. 
                      Please check your email for a confirmation message.
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <AlertCircle className="h-6 w-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-lg font-semibold text-red-800 mb-1">
                      Submission Failed
                    </h3>
                    <p className="text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  autoComplete="off"
                  tabIndex={-1}
                  className="hidden"
                  aria-hidden="true"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-[#4a3a33] font-medium mb-2 block">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      minLength={2}
                      maxLength={100}
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      className="border-[#d4ccc4] focus:border-[#8B2820] focus:ring-[#8B2820]"
                      disabled={loading}
                    />
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-[#4a3a33] font-medium mb-2 block">
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      className="border-[#d4ccc4] focus:border-[#8B2820] focus:ring-[#8B2820]"
                      disabled={loading}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-[#4a3a33] font-medium mb-2 block">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    minLength={10}
                    maxLength={20}
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="border-[#d4ccc4] focus:border-[#8B2820] focus:ring-[#8B2820]"
                    disabled={loading}
                  />
                </div>

                <div>
                  <Label htmlFor="message" className="text-[#4a3a33] font-medium mb-2 block">
                    Your Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    minLength={10}
                    maxLength={1000}
                    rows={6}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your inquiry or requirements..."
                    className="border-[#d4ccc4] focus:border-[#8B2820] focus:ring-[#8B2820] resize-none"
                    disabled={loading}
                  />
                  <p className="text-sm text-[#736c64] mt-2">
                    {formData.message.length}/1000 characters
                  </p>
                </div>

                <Button
                  type="submit"
                  size="lg"
                  disabled={loading}
                  className="w-full bg-[#8B2820] hover:bg-[#6d1f19] text-white py-6 text-lg rounded-md shadow-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="mr-2 h-5 w-5" />
                      Send Message
                    </>
                  )}
                </Button>

                {TURNSTILE_SITE_KEY ? (
                  <div>
                    <div ref={captchaContainerRef} />
                  </div>
                ) : null}

                <p className="text-center text-sm text-[#736c64] mt-4">
                  * Required fields. We respect your privacy and will never share your information.
                </p>
              </form>
            </CardContent>
          </Card>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="p-6">
              <h3 className="font-semibold text-[#8B2820] mb-2">Email Us</h3>
              <p className="text-[#4a3a33]">care@agniyaspices.com</p>
            </div>
            <div className="p-6">
              <h3 className="font-semibold text-[#8B2820] mb-2">Call Us</h3>
              <p className="text-[#4a3a33]">+91 8086 07 5558</p>
            </div>
            <div className="p-6">
              <h3 className="font-semibold text-[#8B2820] mb-2">Visit Us</h3>
              <p className="text-[#4a3a33]">236/2, Convent Road, Lokamaleswaram, Kodungallur, Kerala, India - 680664</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
